/**
 * Decords interaction-triggered product scripts.
 * Product components stay out of the initial page load and are primed on intent.
 */
(() => {
  'use strict';

  if (window.DecordsProductScriptLoader) return;

  const source = document.currentScript || Array.from(document.scripts).reverse().find((script) =>
    /\/quick-add\.js(?:\?|$)/.test(script.src)
  );
  if (!source || !source.src) return;

  const sourceUrl = new URL(source.src);
  const configuredAssets = window.DecordsProductScriptAssets || {};
  const components = {
    productForm: ['product-form', configuredAssets.productForm || 'product-form-core.js'],
    variantPicker: ['variant-picker', configuredAssets.variantPicker || 'variant-picker-core.js'],
    quickAdd: ['quick-add-drawer', configuredAssets.quickAdd || 'quick-add-core.js']
  };
  const requests = new Map();
  const pending = new WeakSet();

  const ready = (key) => Boolean(customElements.get(components[key][0]));

  const resolveAsset = (asset) => {
    if (/^(?:https?:)?\/\//.test(asset)) return asset;
    const url = new URL(asset, sourceUrl);
    url.search = sourceUrl.search;
    return url.href;
  };

  const load = (key) => {
    if (ready(key)) return Promise.resolve();
    if (requests.has(key)) return requests.get(key);

    const request = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = resolveAsset(components[key][1]);
      script.async = false;
      script.dataset.decordsProductCore = key;
      script.onload = resolve;
      script.onerror = () => {
        requests.delete(key);
        reject(new Error(`Unable to load ${script.src}`));
      };
      document.head.appendChild(script);
    });

    requests.set(key, request);
    return request;
  };

  const ensureProductForm = () => load('productForm');
  const ensureVariantPicker = () => load('variantPicker');
  const ensureQuickAdd = () => Promise.all([
    ensureProductForm(),
    ensureVariantPicker()
  ]).then(() => load('quickAdd'));

  const report = (error) => console.error('[Decords product scripts]', error);

  const contains = (root, selector) => Boolean(
    root &&
    ((root.nodeType === 1 && root.matches(selector)) ||
      (root.querySelector && root.querySelector(selector)))
  );

  const scan = (root = document) => {
    if (contains(root, '.js-quick-add')) {
      ensureQuickAdd().catch(report);
      return;
    }
    if (contains(root, 'product-form .js-product-form')) ensureProductForm().catch(report);
    if (contains(root, 'variant-picker')) ensureVariantPicker().catch(report);
  };

  const primeFromTarget = (target) => {
    if (!(target instanceof Element)) return;

    if (target.closest('.js-quick-add')) {
      ensureQuickAdd().catch(report);
      return;
    }

    if (target.closest('product-form [name="add"]')) {
      ensureProductForm().catch(report);
      return;
    }

    if (target.closest('variant-picker')) {
      ensureVariantPicker().catch(report);
    }
  };

  const replay = (element, promise, action, fallback) => {
    if (pending.has(element)) return;
    pending.add(element);

    promise
      .then(() => {
        pending.delete(element);
        if (element.isConnected) action();
      })
      .catch((error) => {
        pending.delete(element);
        report(error);
        if (fallback) fallback();
      });
  };

  const nativeSubmit = (form) => {
    const variantInput = form.querySelector('[name="id"]');
    if (variantInput) variantInput.disabled = false;
    HTMLFormElement.prototype.submit.call(form);
  };

  document.addEventListener('pointerover', (event) => primeFromTarget(event.target), {
    capture: true,
    passive: true
  });
  document.addEventListener('pointerdown', (event) => primeFromTarget(event.target), {
    capture: true,
    passive: true
  });
  document.addEventListener('focusin', (event) => primeFromTarget(event.target), true);

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const quickAddButton = target.closest('.js-quick-add');
    if (quickAddButton && !ready('quickAdd')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      replay(
        quickAddButton,
        ensureQuickAdd(),
        () => quickAddButton.click(),
        () => {
          if (quickAddButton.dataset.productUrl) {
            window.location.assign(quickAddButton.dataset.productUrl);
          }
        }
      );
      return;
    }

    const addButton = target.closest('product-form [name="add"]');
    if (addButton && !ready('productForm')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      replay(
        addButton,
        ensureProductForm(),
        () => addButton.click(),
        () => {
          if (addButton.form) nativeSubmit(addButton.form);
        }
      );
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('product-form') || ready('productForm')) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitter = event.submitter;
    replay(
      form,
      ensureProductForm(),
      () => {
        if (submitter instanceof HTMLElement && submitter.isConnected) {
          form.requestSubmit(submitter);
        } else {
          form.requestSubmit();
        }
      },
      () => nativeSubmit(form)
    );
  }, true);

  document.addEventListener('change', (event) => {
    const control = event.target;
    if (!(control instanceof Element)) return;
    if (!control.closest('variant-picker') || ready('variantPicker')) return;

    event.stopImmediatePropagation();
    replay(
      control,
      ensureVariantPicker(),
      () => control.dispatchEvent(new Event('change', { bubbles: true }))
    );
  }, true);

  window.DecordsProductScriptLoader = Object.freeze({
    scan,
    ensureProductForm,
    ensureVariantPicker,
    ensureQuickAdd
  });
})();
